/* Tiny markdown renderer.
 *
 * We don't pull a full parser — the brief content is server-generated and
 * we control the shape. This handles: headings, paragraphs, bold/italic,
 * unordered + ordered lists, blockquotes, fenced code, inline code, hr.
 *
 * The output is HTML-safe: we escape first, then apply transforms. This is
 * safe to feed to dangerouslySetInnerHTML for trusted backend output.
 */

const RE_FENCE = /```([\s\S]*?)```/g;
const RE_HR = /^---$/gm;

export function renderMarkdown(src: string): string {
  if (!src) return '';
  let s = escapeHtml(src);

  // Code fences first so inner content isn't transformed.
  s = s.replace(RE_FENCE, (_m, code) => `<pre><code>${code.trim()}</code></pre>`);

  // Horizontal rule
  s = s.replace(RE_HR, '<hr/>');

  // Headings
  s = s.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
  s = s.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
  s = s.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  s = s.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  s = s.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  s = s.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // Blockquotes (single-level)
  s = s.replace(/(^|\n)((?:&gt;\s.+(?:\n|$))+)/g, (_m, lead, block: string) => {
    const inner = block
      .split(/\n/)
      .filter((l) => l.trim())
      .map((l) => l.replace(/^&gt;\s?/, ''))
      .join(' ');
    return `${lead}<blockquote>${inner}</blockquote>\n`;
  });

  // Lists (rough but readable)
  s = s.replace(/(?:^|\n)((?:[ \t]*[-*]\s.+(?:\n|$))+)/g, (_m, block: string) => {
    const items = block
      .trim()
      .split(/\n/)
      .map((l) => l.replace(/^[ \t]*[-*]\s+/, ''))
      .map((t) => `<li>${t}</li>`)
      .join('');
    return `\n<ul>${items}</ul>\n`;
  });
  s = s.replace(/(?:^|\n)((?:[ \t]*\d+\.\s.+(?:\n|$))+)/g, (_m, block: string) => {
    const items = block
      .trim()
      .split(/\n/)
      .map((l) => l.replace(/^[ \t]*\d+\.\s+/, ''))
      .map((t) => `<li>${t}</li>`)
      .join('');
    return `\n<ol>${items}</ol>\n`;
  });

  // Inline: bold, italic, code, links
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/_([^_]+)_/g, '<em>$1</em>');
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Paragraphs: wrap remaining bare lines.
  s = s
    .split(/\n{2,}/)
    .map((chunk) => {
      const t = chunk.trim();
      if (!t) return '';
      if (/^<(h\d|ul|ol|pre|blockquote|hr|p)/.test(t)) return t;
      return `<p>${t.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('\n');

  return s;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
